import { Modal, Card, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Rating from '../ui/Rating';
import { useSelector } from 'react-redux';
import { FaFire, FaEye } from 'react-icons/fa';
import '../../assets/styles/product.card.css';
import ProductPrice from './ProductPrice';
import { useAddToCartMutation } from '../../slices/cartApiSlice';
import { useGetProductDetailsQuery } from '../../slices/productApiSlice';
import { FaCartPlus, FaCheck, FaX, FaStore } from 'react-icons/fa6';
import '../../assets/styles/action-feedback.css';
import { feedbackClassName } from '../../hooks/useActionFeedback';
import { useState, useEffect, useRef, useCallback } from 'react';
import ProductDescription from './ProductDescription';
import ProductModalFooter from './ProductModalFooter';
import ProductImage from './ProductImage';
import QuantitySelector from '../ui/QuantitySelector';
import ContificoCodeBadge from '../ui/ContificoCodeBadge';
import { useCreateEventMutation } from '../../slices/productEventsApiSlice';
import { getSessionId } from '../../utils/sessionUtils';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { bufferEvent } from '../../utils/eventBuffer';
import VariationSelector from './VariationSelector';
import { FreeShippingBadge } from './ProductBenefitMarks';
import { buildLinkedVariationPicker, isLinkedVariation } from '../../utils/productUtils';

const VARIATION_FOCUS_RETRY_DELAY_MS = 80;
const VARIATION_FOCUS_MAX_RETRIES = 10;

const ProductCard = ({ product, variant }) => {
    const isCarousel = variant === 'carousel';
    const { userInfo } = useSelector((state) => state.auth);
    const { activeOrder } = useSelector((state) => state.cart);
    const { companiesByRuc } = useSelector((state) => state.companies);

    const [createEvent] = useCreateEventMutation();
    const [cardRef, isVisible] = useIntersectionObserver({
        threshold: 0.5,
    });
    const [hasImpressionSent, setHasImpressionSent] = useState(false);

    useEffect(() => {
        if (isVisible && !hasImpressionSent && product?._id) {
            bufferEvent(
                {
                    type: 'impression',
                    productId: product._id,
                    sessionId: getSessionId(),
                },
                createEvent
            );
            setHasImpressionSent(true);
        }
    }, [isVisible, hasImpressionSent, createEvent, product?._id]);

    const isLowStock =
        userInfo && userInfo?.priceAssigned === 'pvp_distribuidor'
            ? (product?.countInStock || 0) > 0 && (product?.countInStock || 0) <= 25
            : (product?.countInStock || 0) > 0 && (product?.countInStock || 0) <= 10;

    const showExtraDetails = userInfo?.priceAssigned === 'pvp_distribuidor';


    const isOutOfStock = product?.countInStock === 0;
    const inCarts = product?.inCarts || 0;

    const companyData = typeof product.company === 'string'
        ? companiesByRuc[product.company]
        : product.company;

    const companyRuc = companyData?.ruc || 'default';
    const isCompanyLocked = activeOrder?.companies?.[companyRuc]?.checkoutLocked ||
                           (Array.isArray(activeOrder?.companies) && activeOrder.companies.find(c => (c.company?.ruc || 'default') === companyRuc)?.checkoutLocked);

    const showUnitsPerBox =
        Number.isFinite(product?.unitsPerBox) &&
        product?.unitsPerBox > 0 &&
        product?.countInStock >= product?.unitsPerBox * 1.5;

    const isRecentProduct = (() => {
        if (!product?.updatedAt) return false;
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        return new Date(product.updatedAt) >= threeDaysAgo;
    })();

    // Image carousel - professional implementation
    // Combines product.image + product.images for seamless rotation
    const allImages = [product.image, ...(product.images || [])].filter(Boolean);
    const hasMultipleImages = allImages.length > 1;
    const [imageIdx, setImageIdx] = useState(0);
    const autoRotateRef = useRef(null);
    const touchStartRef = useRef(0);

    const rotateImage = (direction) => {
        setImageIdx((prev) => (prev + direction + allImages.length) % allImages.length);
    };

    const goToImage = (idx) => {
        setImageIdx(Math.max(0, Math.min(idx, allImages.length - 1)));
    };

    const startAutoRotate = () => {
        if (!hasMultipleImages || autoRotateRef.current) return;
        autoRotateRef.current = setInterval(() => {
            setImageIdx((prev) => (prev + 1) % allImages.length);
        }, 3500);
    };

    const stopAutoRotate = () => {
        if (autoRotateRef.current) {
            clearInterval(autoRotateRef.current);
            autoRotateRef.current = null;
        }
    };

    const handleMouseEnter = () => {
        // Keep rotation going smoothly on hover
        if (!autoRotateRef.current && hasMultipleImages) {
            startAutoRotate();
        }
    };

    const handleMouseLeave = () => {
        // Keep current image — do not snap back to first image
    };

    const handleTouchStart = (e) => {
        touchStartRef.current = e.touches[0].clientX;
        stopAutoRotate();
    };

    const handleTouchEnd = (e) => {
        const touchEnd = e.changedTouches[0].clientX;
        const diff = touchStartRef.current - touchEnd;
        if (Math.abs(diff) > 50) {
            rotateImage(diff > 0 ? 1 : -1);
        }
        startAutoRotate();
    };

    // Auto-rotate images on card mount
    useEffect(() => {
        if (!hasMultipleImages) return;

        const intervalId = setInterval(() => {
            setImageIdx((prev) => (prev + 1) % allImages.length);
        }, 3500);

        autoRotateRef.current = intervalId;

        return () => {
            clearInterval(intervalId);
            autoRotateRef.current = null;
        };
    }, [hasMultipleImages, allImages.length]);

    const [showModal, setShowModal] = useState(false);
    const [shouldAnimateVariations, setShouldAnimateVariations] = useState(false);
    const [mainImage, setMainImage] = useState(null);
    const [selectedVariations, setSelectedVariations] = useState({});
    const [linkedVariationOrderMap, setLinkedVariationOrderMap] = useState({});
    const [linkedMoveHint, setLinkedMoveHint] = useState({
        variationType: null,
        optionLabel: '',
    });
    const [qty, setQty] = useState(1);
    const [modalProductId, setModalProductId] = useState(product._id);
    const modalDetailsRef = useRef(null);

    const { data: linkedModalProduct } = useGetProductDetailsQuery(modalProductId, {
        skip: !showModal || !modalProductId || modalProductId === product._id,
    });

    const modalProduct = modalProductId === product._id ? product : linkedModalProduct;

    const variationRef = useRef(null);
    const variationFocusRetryRef = useRef(null);

    const clearVariationFocusRetry = useCallback(() => {
        if (variationFocusRetryRef.current) {
            clearTimeout(variationFocusRetryRef.current);
            variationFocusRetryRef.current = null;
        }
    }, []);

    const focusVariationOptions = useCallback((attempt = 0) => {
        const variationContainer = variationRef.current;
        const firstChip = variationContainer?.querySelector('.variation-chip-button:not([disabled])');

        if (variationContainer && firstChip) {
            variationContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstChip.focus({ preventScroll: true });
            clearVariationFocusRetry();
            return;
        }

        if (attempt >= VARIATION_FOCUS_MAX_RETRIES) {
            clearVariationFocusRetry();
            return;
        }

        clearVariationFocusRetry();
        variationFocusRetryRef.current = setTimeout(() => {
            focusVariationOptions(attempt + 1);
        }, VARIATION_FOCUS_RETRY_DELAY_MS);
    }, [clearVariationFocusRetry]);

    useEffect(() => {
        if (!showModal || !modalProduct?._id) return;

        setMainImage(modalProduct.image);
        setQty(1);

        const nextLinkedOrderMap = { ...linkedVariationOrderMap };
        let hasNewLinkedOrder = false;
        const initialVariations = {};
        modalProduct.variations?.forEach((v) => {
            if (v.options?.length > 0) {
                if (isLinkedVariation(v)) {
                    if (!nextLinkedOrderMap[v.variationType]?.length) {
                        nextLinkedOrderMap[v.variationType] = buildLinkedVariationPicker(modalProduct, v)
                            .map((option) => option.id);
                        hasNewLinkedOrder = true;
                    }
                    const picker = buildLinkedVariationPicker(
                        modalProduct,
                        v,
                        nextLinkedOrderMap[v.variationType]
                    );
                    const currentOption = picker.find((option) => option.id === modalProduct._id);
                    initialVariations[v.variationType] = {
                        name: currentOption?.label || modalProduct.name,
                        linkedProduct: modalProduct._id,
                    };
                } else {
                    initialVariations[v.variationType] = v.options[0];
                }
            }
        });
        setSelectedVariations(initialVariations);
        if (hasNewLinkedOrder) {
            setLinkedVariationOrderMap(nextLinkedOrderMap);
        }
    }, [showModal, modalProduct, linkedVariationOrderMap]);

    useEffect(() => {
        if (!showModal || !shouldAnimateVariations) return;

        focusVariationOptions();

        const animationTimer = setTimeout(() => {
            setShouldAnimateVariations(false);
        }, 3000);

        return () => {
            clearVariationFocusRetry();
            clearTimeout(animationTimer);
        };
    }, [showModal, shouldAnimateVariations, modalProduct?._id, focusVariationOptions, clearVariationFocusRetry]);

    useEffect(() => () => {
        clearVariationFocusRetry();
    }, [clearVariationFocusRetry]);

    useEffect(() => {
        if (!linkedMoveHint.variationType) return;

        const clearHintTimer = setTimeout(() => {
            setLinkedMoveHint({ variationType: null, optionLabel: '' });
        }, 2200);

        return () => clearTimeout(clearHintTimer);
    }, [linkedMoveHint]);

    const selectedVariation = Object.values(selectedVariations).slice(-1)[0] || null;
    const modalDisplayProduct = modalProduct || product || {};
    const modalUnitsPerBox = Number(modalDisplayProduct?.unitsPerBox || 0);
    const modalShowUnitsPerBox =
        Number.isFinite(modalUnitsPerBox) &&
        modalUnitsPerBox > 0 &&
        Number(modalDisplayProduct?.countInStock || 0) >= modalUnitsPerBox * 1.5;

    const displayImage = selectedVariation?.image || mainImage || modalProduct?.image || '/images/default.jpg';

    const modalCompanyData = typeof modalProduct?.company === 'string'
        ? companiesByRuc[modalProduct.company]
        : modalProduct?.company;

    const modalCompanyRuc = modalCompanyData?.ruc || 'default';
    const isModalCompanyLocked = activeOrder?.companies?.[modalCompanyRuc]?.checkoutLocked ||
        (Array.isArray(activeOrder?.companies) && activeOrder.companies.find(c => (c.company?.ruc || 'default') === modalCompanyRuc)?.checkoutLocked);

    const handleClose = () => {
        setShowModal(false);
        setShouldAnimateVariations(false);
        setLinkedVariationOrderMap({});
        setLinkedMoveHint({ variationType: null, optionLabel: '' });
        setModalProductId(product._id);
    };

    const buildInitialLinkedVariationOrder = useCallback((sourceProduct) => {
        const initialLinkedOrder = {};
        sourceProduct?.variations?.forEach((variation) => {
            if (isLinkedVariation(variation)) {
                initialLinkedOrder[variation.variationType] = buildLinkedVariationPicker(sourceProduct, variation)
                    .map((option) => option.id);
            }
        });
        return initialLinkedOrder;
    }, []);

    const handleShow = (e) => {
        e.preventDefault();
        e.stopPropagation();
        bufferEvent(
            {
                type: 'click',
                productId: product._id,
                sessionId: getSessionId(),
            },
            createEvent
        );
        setLinkedVariationOrderMap(buildInitialLinkedVariationOrder(product));
        setModalProductId(product._id);
        setShowModal(true);
    };

    const handleProductClick = () => {
        bufferEvent(
            {
                type: 'click',
                productId: product._id,
                sessionId: getSessionId(),
            },
            createEvent
        );
    };

    const hasVariations = product.variations && product.variations.length > 0;

    const [addToCart] = useAddToCartMutation();
    const [cardCartFeedback, setCardCartFeedback] = useState(null);
    const [modalCartFeedback, setModalCartFeedback] = useState(null);
    const cardFeedbackTimerRef = useRef(null);
    const modalFeedbackTimerRef = useRef(null);

    const flashCartFeedback = (target, status) => {
        const setFeedback = target === 'modal' ? setModalCartFeedback : setCardCartFeedback;
        const timerRef = target === 'modal' ? modalFeedbackTimerRef : cardFeedbackTimerRef;
        setFeedback(status);
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
        }
        timerRef.current = window.setTimeout(() => {
            setFeedback(null);
            timerRef.current = null;
        }, 1200);
    };

    const addToCartDirectlyHandler = async () => {
        const activeProduct = showModal ? modalDisplayProduct : product;
        const activeCompanyLocked = showModal ? isModalCompanyLocked : isCompanyLocked;
        const feedbackTarget = showModal ? 'modal' : 'card';

        if (activeCompanyLocked) {
            flashCartFeedback(feedbackTarget, 'error');
            return;
        }
        if (hasVariations && !showModal) {
            setLinkedVariationOrderMap(buildInitialLinkedVariationOrder(product));
            setShouldAnimateVariations(true);
            setShowModal(true);
            return;
        }

        const countInStock = activeProduct?.countInStock || 0;

        if (countInStock > 0) {
            bufferEvent(
                {
                    type: 'click',
                    productId: activeProduct._id,
                    sessionId: getSessionId(),
                },
                createEvent
            );

            try {
                let variationPayload = null;
                if ((activeProduct?.variations || []).length > 0) {
                    const firstNonLinkedVariation = Object.entries(selectedVariations).find(([, option]) => !option?.linkedProduct);
                    if (firstNonLinkedVariation) {
                        const [firstType, firstOption] = firstNonLinkedVariation;
                        variationPayload = {
                            variationType: firstType,
                            option: firstOption.name,
                            image: firstOption.image || null,
                        };
                    }
                }

                const itemToAdd = {
                    productId: activeProduct._id,
                    qty: showModal ? qty : 1,
                    variation: variationPayload,
                };

                if (showModal) {
                    setModalCartFeedback('pending');
                } else {
                    setCardCartFeedback('pending');
                }
                await addToCart(itemToAdd).unwrap();
                flashCartFeedback(feedbackTarget, 'success');
                if (showModal) {
                    window.setTimeout(() => setShowModal(false), 700);
                }
            } catch {
                flashCartFeedback(feedbackTarget, 'error');
            }
        } else {
            flashCartFeedback(feedbackTarget, 'error');
        }
    };

    return (
        <>
            <Link
                to={`/product/${product._id}`}
                className="text-decoration-none text-dark"
                onClick={handleProductClick}
            >
                <Card
                    className={`ProductCard${isCarousel ? ' ProductCard--carousel' : ''}${isLowStock || isOutOfStock ? ' ProductCard--has-stock-tag' : ''}`}
                    ref={cardRef}
                >
                    <div
                        className="product-card-img"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onTouchStart={isCarousel ? undefined : handleTouchStart}
                        onTouchEnd={isCarousel ? undefined : handleTouchEnd}
                    >
                        <Card.Img
                            src={allImages[imageIdx] || '/images/default.jpg'}
                            variant="top"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/images/default.jpg';
                            }}
                        />
                        {isRecentProduct && (
                            <span className="new-dot-badge" aria-label="Nuevo producto" />
                        )}
                        <FreeShippingBadge product={product} className="product-card-free-shipping" />
                        {inCarts > 0 && (
                            <div className="ProductCard-incarts-badge">
                                <FaFire className="incarts-icon" aria-hidden="true" />
                                <span className="incarts-text">
                                    en {inCarts} carrito{inCarts > 1 ? 's' : ''}!
                                </span>
                            </div>
                        )}
                        {isCarousel ? (
                            <div className="ProductCard-carousel-actions">
                                <button
                                    className="quick-view-btn"
                                    onClick={handleShow}
                                    aria-label="Vista rápida"
                                    type="button"
                                >
                                    <FaEye size={15} />
                                </button>
                                <button
                                    className={`ProductCard-icon-button ProductCard-carousel-cart ${isCompanyLocked ? 'disabled' : ''} ${!hasVariations && cardCartFeedback === 'success' ? 'is-added' : ''} ${feedbackClassName(hasVariations ? null : cardCartFeedback)}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        addToCartDirectlyHandler();
                                    }}
                                    disabled={isCompanyLocked || (!hasVariations && (cardCartFeedback === 'pending' || cardCartFeedback === 'success'))}
                                    aria-label={hasVariations ? 'Ver opciones' : 'Agregar al carrito'}
                                    type="button"
                                >
                                    {!hasVariations && cardCartFeedback === 'success' ? (
                                        <FaCheck size={15} />
                                    ) : (
                                        <FaCartPlus size={15} />
                                    )}
                                </button>
                            </div>
                        ) : (
                            <button
                                className="quick-view-btn"
                                onClick={handleShow}
                                aria-label="Vista rápida"
                            >
                                <FaEye size={18} />
                            </button>
                        )}

                        {/* Image navigation - listing cards only. Carousel keeps auto-rotate. */}
                        {!isCarousel && hasMultipleImages && (
                            <>
                                <button
                                    className="product-img-btn product-img-btn-prev"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        rotateImage(-1);
                                        stopAutoRotate();
                                        startAutoRotate();
                                    }}
                                    aria-label="Imagen anterior"
                                    type="button"
                                >
                                    ‹
                                </button>
                                <button
                                    className="product-img-btn product-img-btn-next"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        rotateImage(1);
                                        stopAutoRotate();
                                        startAutoRotate();
                                    }}
                                    aria-label="Próxima imagen"
                                    type="button"
                                >
                                    ›
                                </button>

                                {/* Image indicators */}
                                <div className="product-img-indicators">
                                    {allImages.map((_, i) => (
                                        <button
                                            key={i}
                                            className={`product-img-indicator${i === imageIdx ? ' active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                goToImage(i);
                                                stopAutoRotate();
                                                startAutoRotate();
                                            }}
                                            aria-label={`Imagen ${i + 1}`}
                                            type="button"
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {isCarousel ? (
                        <Card.Body className="ProductCard-body">
                            <div className="ProductCard-name" title={product.name}>
                                {product.name}
                            </div>
                            <ProductPrice
                                product={product}
                                userInfo={userInfo}
                                size="md"
                                className="ProductCard-price"
                            />
                        </Card.Body>
                    ) : (
                    <Card.Body
                        className={`ProductCard-body ${!hasVariations ? 'no-variations' : ''}`}
                    >
                        <div
                            className={`ProductCard-title-row ${hasVariations ? 'ProductCard-title-row--has-options' : ''}`}
                        >
                            <div className="ProductCard-title-content">
                                <div className="ProductCard-name">{product.name}</div>
                            </div>
                            <button
                                className={`ProductCard-icon-button ProductCard-title-action ${hasVariations ? 'ProductCard-action-button--options' : ''} ${isCompanyLocked ? 'disabled' : ''} ${!hasVariations && cardCartFeedback === 'success' ? 'is-added' : ''} ${feedbackClassName(hasVariations ? null : cardCartFeedback)}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addToCartDirectlyHandler();
                                }}
                                disabled={isCompanyLocked || (!hasVariations && (cardCartFeedback === 'pending' || cardCartFeedback === 'success'))}
                                aria-label={hasVariations ? 'Ver opciones' : 'Agregar al carrito'}
                            >
                                {hasVariations ? (
                                    <span className="ProductCard-options-label">
                                        <span>Ver</span>
                                        <span className="ProductCard-options-label-second">
                                            opciones
                                        </span>
                                    </span>
                                ) : cardCartFeedback === 'success' ? (
                                    <FaCheck size={16} />
                                ) : (
                                    <FaCartPlus style={{ position: 'relative' }} size={16} />
                                )}
                            </button>
                        </div>
                        {companyData && (
                            <div className="ProductCard-meta-row">
                                <Link
                                    to={`/company/${companyData.ruc}`}
                                    className="seller-chip"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <FaStore size={9} />
                                    <span>{companyData.tradeName}</span>
                                </Link>
                            </div>
                        )}
                        {showExtraDetails && (
                            <div className="distributor-info-row">
                                <div className="distributor-stock-info">
                                    <div className="stock-info-content">
                                        <div className="stock-info-label">Stock</div>
                                        <div className="stock-info-value">
                                            {product.countInStock}
                                        </div>
                                    </div>
                                    <div
                                        className={`stock-info-indicator ${
                                            product.countInStock > 50
                                                ? 'stock-high'
                                                : product.countInStock > 25
                                                  ? 'stock-medium'
                                                  : product.countInStock > 0
                                                    ? 'stock-low'
                                                    : 'stock-out'
                                        }`}
                                    ></div>
                                </div>

                                {showUnitsPerBox && (
                                    <div className="units-per-box-info">
                                        <div className="units-info-content">
                                            <div className="units-info-label">Por Caja</div>
                                            <div className="units-info-value">
                                                {product.unitsPerBox}
                                            </div>
                                        </div>
                                        <div className="units-info-icon">📦</div>
                                    </div>
                                )}
                            </div>
                        )}

                        <ProductPrice product={product} userInfo={userInfo} size="lg" className="ProductCard-price" />
                    </Card.Body>
                    )}
                    {isLowStock && (
                        <div className="low-stock-tag text-center">
                            ¡Solo {product.countInStock}{' '}
                            {product.countInStock === 1 ? 'disponible' : 'disponibles'}!
                        </div>
                    )}
                    {isOutOfStock && <div className="out-of-stock-tag">Agotado</div>}
                </Card>
            </Link>
            <Modal
                show={showModal}
                onHide={handleClose}
                onEntered={() => {
                    if (shouldAnimateVariations) {
                        focusVariationOptions();
                    }
                }}
                centered
                size="lg"
                className="product-quick-view-modal"
            >
                <Modal.Body className="p-0 overflow-hidden">
                    {!modalProduct?._id ? (
                        <div className="p-5 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                    ) : (
                        <Row className="g-0">
                            <Col
                                lg={6}
                                className="modal-image-col bg-light d-flex align-items-center justify-content-center p-4 position-relative overflow-hidden"
                            >
                                <div className="modal-image-container w-100">
                                    <div className="modal-badge-container">
                                        {(modalProduct?.inCarts || 0) > 0 && (
                                            <div
                                                className="modal-badge badge-hot"
                                                style={{
                                                    backgroundColor: '#ff4500',
                                                    color: 'white',
                                                }}
                                            >
                                                🔥 EN {modalProduct.inCarts}{' '}
                                                {modalProduct.inCarts === 1
                                                    ? 'CARRITO'
                                                    : 'CARRITOS'}
                                            </div>
                                        )}
                                        {modalDisplayProduct.countInStock === 0 ? (
                                            <div className="modal-badge badge-out">AGOTADO</div>
                                        ) : (
                                            (userInfo &&
                                            userInfo?.priceAssigned === 'pvp_distribuidor'
                                                ? modalDisplayProduct.countInStock > 0 &&
                                                  modalDisplayProduct.countInStock <= 25
                                                : modalDisplayProduct.countInStock > 0 &&
                                                  modalDisplayProduct.countInStock <= 10) && (
                                                <div className="modal-badge badge-low">
                                                    ¡SOLO {modalDisplayProduct.countInStock}{' '}
                                                    {modalDisplayProduct.countInStock === 1
                                                        ? 'DISPONIBLE'
                                                        : 'DISPONIBLES'}
                                                    !
                                                </div>
                                            )
                                        )}
                                        {showExtraDetails && modalShowUnitsPerBox && (
                                            <div className="modal-badge badge-box">
                                                Caja X{modalProduct.unitsPerBox}
                                            </div>
                                        )}
                                    </div>
                                    <ProductImage
                                        src={displayImage}
                                        images={[
                                            modalProduct.image,
                                            ...(modalProduct.images || []),
                                        ]}
                                        alt={modalProduct.name}
                                        className="modal-main-img"
                                        sideThumbnails={false}
                                        zoomTargetRef={modalDetailsRef}
                                    />
                                </div>
                            </Col>

                            <Col
                                lg={6}
                                className="p-4 p-md-5 d-flex flex-column modal-details-col"
                                ref={modalDetailsRef}
                            >
                                <button className="modal-close-icon" onClick={handleClose}>
                                    <FaX size={14} />
                                </button>
                                <div className="modal-header-section mb-2">
                                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                                        <Rating
                                            value={modalProduct.rating}
                                            text={`(${modalProduct.numReviews})`}
                                            size="lg"
                                            className="ProductModal-rating"
                                        />
                                    </div>
                                    <Link
                                        to={`/product/${modalProduct._id}`}
                                        className="text-decoration-none"
                                    >
                                        <h2 className="modal-product-title mb-1">
                                            {modalProduct.name}
                                        </h2>
                                    </Link>
                                    <FreeShippingBadge product={modalProduct} />
                                    {modalCompanyData && (
                                        <div className="modal-seller-row">
                                            <Link
                                                to={`/company/${modalCompanyData.ruc}`}
                                                className="seller-chip seller-chip--badge"
                                            >
                                                <FaStore size={10} />
                                                <span>
                                                    {modalCompanyData.tradeName}
                                                    {modalCompanyData.city && ` · ${modalCompanyData.city}`}
                                                </span>
                                            </Link>
                                        </div>
                                    )}
                                    <div className="modal-price-box d-flex align-items-baseline gap-2">
                                        <ProductPrice
                                            product={modalDisplayProduct}
                                            userInfo={userInfo}
                                            size="xl"
                                        />
                                        {modalDisplayProduct.countInStock === 0 && (
                                            <span className="text-danger fw-bold modal-stock-note">
                                                SIN STOCK
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-non-scrollable-content mb-2">
                                    <VariationSelector
                                        ref={variationRef}
                                        product={modalProduct}
                                        selectedVariations={selectedVariations}
                                        setSelectedVariations={setSelectedVariations}
                                        animateFocus={shouldAnimateVariations}
                                        linkedVariationOrderMap={linkedVariationOrderMap}
                                        userInfo={userInfo}
                                        movedLinkedVariationType={linkedMoveHint.variationType}
                                        movedLinkedVariationLabel={linkedMoveHint.optionLabel}
                                        onVariationChange={(
                                            selectedOption,
                                            _newVariations,
                                            meta
                                        ) => {
                                            if (meta?.isLinkedVariation) {
                                                if (
                                                    meta.selectedId &&
                                                    meta.selectedId !== modalProduct._id
                                                ) {
                                                    setLinkedMoveHint({
                                                        variationType: meta.variationType,
                                                        optionLabel: selectedOption?.name || '',
                                                    });
                                                    setModalProductId(meta.selectedId);
                                                    setMainImage(null);
                                                }
                                                return;
                                            }

                                            if (selectedOption?.image) {
                                                setMainImage(selectedOption.image);
                                            }
                                        }}
                                    />
                                </div>

                                <div className="modal-scrollable-content pe-2 mb-2">
                                    <div className="modal-info-section">
                                        <ProductDescription
                                            description={modalProduct.description}
                                        />
                                        <ContificoCodeBadge
                                            code={modalDisplayProduct?.contificoCode}
                                            className="mt-3"
                                        />
                                    </div>
                                </div>

                                <div className="modal-action-section mt-auto pt-3 border-top">
                                    {modalDisplayProduct?.countInStock > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {showExtraDetails && (
                                                <div className="modal-distributor-info-row">
                                                    <div className="modal-stock-info">
                                                        <div className="modal-stock-content">
                                                            <div className="modal-stock-label">
                                                                Stock Disponible
                                                            </div>
                                                            <div className="modal-stock-value">
                                                                {modalDisplayProduct?.countInStock}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`modal-stock-indicator ${
                                                                modalDisplayProduct?.countInStock >
                                                                50
                                                                    ? 'stock-high'
                                                                    : modalDisplayProduct?.countInStock >
                                                                        25
                                                                      ? 'stock-medium'
                                                                      : modalDisplayProduct?.countInStock >
                                                                          0
                                                                        ? 'stock-low'
                                                                        : 'stock-out'
                                                            }`}
                                                        ></div>
                                                    </div>

                                                    {modalShowUnitsPerBox && (
                                                        <div className="modal-units-info">
                                                            <div className="modal-units-content">
                                                                <div className="modal-units-label">
                                                                    Unidades x Caja
                                                                </div>
                                                                <div className="modal-units-value">
                                                                    {modalProduct.unitsPerBox}
                                                                </div>
                                                            </div>
                                                            <div className="modal-units-icon">
                                                                📦
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="d-none d-md-flex align-items-center justify-content-between bg-light p-2 rounded-3">
                                                <span className="fw-bold ps-2 text-uppercase letter-spacing-1 modal-qty-label">
                                                    Cantidad
                                                </span>
                                                <QuantitySelector
                                                    value={qty}
                                                    max={modalDisplayProduct?.countInStock}
                                                    onChange={(val) => setQty(val)}
                                                    size="md"
                                                />
                                            </div>

                                            <div className="d-flex gap-2">
                                                <div className="modal-desktop-footer w-100">
                                                    <ProductModalFooter
                                                        product={modalProduct}
                                                        onClose={handleClose}
                                                        addToCart={addToCartDirectlyHandler}
                                                        disabled={isModalCompanyLocked}
                                                        feedback={modalCartFeedback}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center p-3 bg-light rounded-3 text-danger fw-bold">
                                            PRODUCTO AGOTADO
                                        </div>
                                    )}
                                </div>

                                {modalDisplayProduct?.countInStock > 0 && (
                                    <div className="modal-mobile-sticky-footer d-md-none">
                                        <ProductModalFooter
                                            product={modalProduct}
                                            onClose={handleClose}
                                            addToCart={addToCartDirectlyHandler}
                                            disabled={isModalCompanyLocked}
                                            feedback={modalCartFeedback}
                                            layout="inline"
                                            quantityValue={qty}
                                            quantityMax={modalDisplayProduct?.countInStock}
                                            onQuantityChange={(val) => setQty(val)}
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ProductCard;
